import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { AccountDisabledNotification } from 'src/components/common/AccountDisabledNotification';
import { AccountInactiveNotification } from 'src/components/common/AccountInactiveNotification';
import { EventListing } from 'src/components/Events/EventListing';
import { PerformerVenueList } from 'src/components/Events/PerformerVenueList';
import { MainFilterBar } from 'src/components/MainFilterBar';
import { SaleFilterBar } from 'src/components/Sales/SaleFilterBar';
import { SaleSearchBox } from 'src/components/Sales/SaleFilterBar/SaleSearchBox';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { MultiSelectionContextProvider } from 'src/contexts/MultiSelectionContext';
import { ExplorerLayout } from 'src/core/layouts';
import { Button, Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { createSaleSearchPageUrl } from 'src/utils/advancedSearchUtils';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  CatalogEntity,
  Event,
  EventGroupingType,
  Feature,
  Sale,
  SalesViewMode,
} from 'src/WebApiController';
import Swiper from 'swiper';

import { MainRoute } from '../MainRoute';

export function SalesEventsExplorer({
  onPerformerVenueRendered,
  onEventsRendered,
}: {
  onPerformerVenueRendered?: (
    items: CatalogEntity[],
    itemType: EventGroupingType
  ) => void;
  onEventsRendered?: (items: Event[]) => void;
}) {
  const routeTitle = useContent(ContentId.Sales);
  const onItemsRendered = (items: EventAccordionItemProps<Sale>[]) => {
    onEventsRendered?.(items.flatMap((i) => i?.event ?? []));
  };

  const { filterQuery } = useFilterQueryContext();

  const swiperRef = useRef<Swiper>();
  const mobile = useMatchMedia('mobile');

  const navigate = useNavigate();
  const isConsignorMasterView = useUserHasConsignorMasterUI();

  const hasSearchV2Feature = useUserHasFeature(Feature.AdvancedSearchV2);

  const onAdvancePurchaseSearch = useCallback(() => {
    const searchUrl = createSaleSearchPageUrl(hasSearchV2Feature);

    setTimeout(() => {
      navigate(searchUrl, {
        state: {
          returnUrl: `${location.pathname}${location.search}`,
        },
      });
    }, 10);
  }, [hasSearchV2Feature, navigate]);

  return (
    <CatalogMultiSelectionContextProvider type="sale">
      <MultiSelectionContextProvider
        isFlattenedView={filterQuery.viewMode === SalesViewMode.FlattenedView}
      >
        <DialogProvider>
          <LayoutContent
            mainRoute={MainRoute.Sales}
            routeTitle={routeTitle}
            centerContent={
              <Stack gap="m" alignItems="center" justifyContent="center">
                <SaleSearchBox />
                {!isConsignorMasterView && (
                  <Button variant="text" onClick={onAdvancePurchaseSearch}>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <Content id={ContentId.AdvancedSearch} />
                    </div>
                  </Button>
                )}
              </Stack>
            }
          >
            <AccountDisabledNotification />
            <AccountInactiveNotification />
            <MainFilterBar swiperRef={swiperRef} />
            <ExplorerLayout
              swiperRef={swiperRef}
              showExpandButton
              leftPanelContent={
                <PerformerVenueList
                  entityType={ActionOutboxEntityType.Sale}
                  onItemsRendered={onPerformerVenueRendered}
                  swiperRef={swiperRef}
                />
              }
            >
              <Stack direction="column" width="full" height="full">
                {mobile && <SaleFilterBar swiperRef={swiperRef} isSearchOnly />}
                <EventListing onItemsRendered={onItemsRendered} />
              </Stack>
            </ExplorerLayout>
          </LayoutContent>
        </DialogProvider>
      </MultiSelectionContextProvider>
    </CatalogMultiSelectionContextProvider>
  );
}
